import { useState } from "react";
import { Phone, MessageCircle, MapPin, Mail, Clock, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) { toast.error("Please fill in name and phone"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, formData);
      const msg = encodeURIComponent(`*New Bulk Order Enquiry*\n\n*Name:* ${formData.name}\n*Phone:* ${formData.phone}\n*Email:* ${formData.email || "N/A"}\n*Requirement:*\n${formData.message || "N/A"}`);
      window.open(`https://wa.me/919104054250?text=${msg}`, "_blank");
      toast.success("Enquiry submitted!");
      setSubmitted(true);
      setFormData({ name: "", phone: "", email: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) { toast.error("Failed to submit. Try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-ivory pt-24">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-champagne-light to-cream">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold mb-4">Get in Touch</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6">Private <span className="text-gold-gradient">Enquiry</span></h1>
          <p className="font-body text-charcoal/60 max-w-xl mx-auto">We're here for retail and wholesale enquiries</p>
        </div>
      </section>

      {/* Form & Contact */}
      <section className="py-16 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Enquiry Form */}
            <div className="bg-gradient-to-br from-champagne-light to-champagne border border-gold-muted/20 p-8">
              <p className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold mb-2">For Trade & Bulk</p>
              <h2 className="font-serif text-2xl text-charcoal mb-2">Bulk Order <span className="text-gold-gradient">Enquiry</span></h2>
              <p className="font-body text-charcoal/50 text-sm mb-6">We'll connect with you on WhatsApp</p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 border border-royal-gold flex items-center justify-center"><CheckCircle size={32} className="text-royal-gold" /></div>
                  <h3 className="font-serif text-xl text-charcoal mb-2">Thank You!</h3>
                  <p className="font-body text-charcoal/60 text-sm">We'll respond on WhatsApp shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="bulk-enquiry-form">
                  <div>
                    <Label className="font-display text-xs tracking-widest uppercase text-charcoal/70">Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your full name" data-testid="enquiry-name-input" className="bg-white border-gold-muted/20" required />
                  </div>
                  <div>
                    <Label className="font-display text-xs tracking-widest uppercase text-charcoal/70">Phone *</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" data-testid="enquiry-phone-input" className="bg-white border-gold-muted/20" required />
                  </div>
                  <div>
                    <Label className="font-display text-xs tracking-widest uppercase text-charcoal/70">Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" data-testid="enquiry-email-input" className="bg-white border-gold-muted/20" />
                  </div>
                  <div>
                    <Label className="font-display text-xs tracking-widest uppercase text-charcoal/70">Requirements</Label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Describe your requirements..." data-testid="enquiry-message-input" rows={4} className="bg-white border-gold-muted/20 resize-none" />
                  </div>
                  <Button type="submit" disabled={submitting} data-testid="enquiry-submit-btn" className="w-full btn-luxury flex items-center justify-center space-x-2 py-4">
                    {submitting ? <span>Submitting...</span> : <><Send size={16} /><span>Submit Enquiry</span></>}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div><p className="font-display text-xs tracking-[0.4em] uppercase text-silver-dark mb-2">Direct Contact</p><h2 className="font-serif text-2xl text-charcoal">Reach <span className="text-gold-gradient">Us</span></h2></div>

              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-5 bg-white border border-gold-muted/10 hover:border-royal-gold/30 transition-colors">
                  <div className="w-12 h-12 border border-royal-gold/30 flex items-center justify-center"><MapPin size={20} className="text-royal-gold" /></div>
                  <div><h3 className="font-display text-xs tracking-widest uppercase text-charcoal mb-2">Showroom</h3><p className="font-body text-sm text-charcoal/60">Shree Ganesh Ornaments<br />1396, Devadinni Pole, Rangati Bajar Road,<br />M.G Haveli Road, Manekchowk,<br />Ahmedabad – 380001</p></div>
                </div>

                <a href="tel:+919104054259" data-testid="contact-call-btn" className="flex items-start space-x-4 p-5 bg-white border border-gold-muted/10 hover:border-royal-gold/30 transition-colors group">
                  <div className="w-12 h-12 border border-royal-gold/30 flex items-center justify-center group-hover:bg-royal-gold/5"><Phone size={20} className="text-royal-gold" /></div>
                  <div><h3 className="font-display text-xs tracking-widest uppercase text-charcoal mb-1">Call Us</h3><p className="font-body text-charcoal/80">+91 9104054259</p></div>
                </a>

                <a href="https://wa.me/919104054259" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="flex items-start space-x-4 p-5 bg-white border border-[#25D366]/20 hover:border-[#25D366]/50 transition-colors group">
                  <div className="w-12 h-12 border border-[#25D366]/30 flex items-center justify-center group-hover:bg-[#25D366]/5"><MessageCircle size={20} className="text-[#25D366]" /></div>
                  <div><h3 className="font-display text-xs tracking-widest uppercase text-charcoal mb-1">WhatsApp</h3><p className="font-body text-charcoal/80">+91 9104054259</p></div>
                </a>

                <a href="mailto:info@shreeganeshornaments.com" className="flex items-start space-x-4 p-5 bg-white border border-gold-muted/10 hover:border-royal-gold/30 transition-colors">
                  <div className="w-12 h-12 border border-silver/30 flex items-center justify-center"><Mail size={20} className="text-silver-dark" /></div>
                  <div><h3 className="font-display text-xs tracking-widest uppercase text-charcoal mb-1">Email</h3><p className="font-body text-charcoal/80">info@shreeganeshornaments.com</p></div>
                </a>

                <div className="flex items-start space-x-4 p-5 bg-white border border-gold-muted/10">
                  <div className="w-12 h-12 border border-charcoal/10 flex items-center justify-center"><Clock size={20} className="text-charcoal/50" /></div>
                  <div><h3 className="font-display text-xs tracking-widest uppercase text-charcoal mb-1">Hours</h3><p className="font-body text-sm text-charcoal/60">Mon – Sat: 10 AM – 8 PM<br />Sunday: Closed</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="luxury-frame">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3672.048096983883!2d72.58790937539647!3d23.02318871706437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e844bdd1e2c65%3A0xc98065b89425aa0!2sManek%20Chowk%20Rd%2C%20Old%20City%2C%20Khadia%2C%20Ahmedabad%2C%20Gujarat%20380001!5e0!3m2!1sen!2sin!4v1706000000000!5m2!1sen!2sin" width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Location" data-testid="google-map" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
